/**
 * GAP event detection engine (Atlas 3.0 §L1 + §L3.3).
 *
 * Six-step algorithm:
 *   1. Collect all compliance-required events (G1–G5 + manifest-declared compliance events)
 *   2. Cross-reference with manifest event catalog
 *   3. Identify missing events by type
 *   4. Check cloud function coverage (which functions emit them)
 *   5. Score gap severity (critical / high / medium)
 *   6. Produce remediation suggestions
 *
 * The G1–G5 events are pre-defined and required for GA.
 * Additional gaps are derived from entities that have `legalBasis` or
 * `complianceRef` metadata but whose corresponding events are absent.
 */
import type { AtlasManifest, EventType } from '@atlas-manifest/types';
import { GAP_EVENTS } from './compliance.js';

// ─── Result types ─────────────────────────────────────────────────────────────

export type GapSeverity = 'critical' | 'high' | 'medium' | 'low';

export interface GapFinding {
  /** Gap identifier, e.g. 'G1' or a derived id like 'derived-consent-v3'. */
  id: string;
  /** The event type that must exist, e.g. 'user_data_export_completed'. */
  requiredEventType: string;
  /** Compliance regulation requiring this event. */
  complianceRef: string;
  /** Human description of the gap and its impact. */
  description: string;
  severity: GapSeverity;
  /** Whether the event type is listed in the manifest event catalog. */
  inManifest: boolean;
  /** Manifest event definition, when found. */
  manifestEvent: EventType | null;
  /** Cloud functions that emit this event. */
  emittedBy: string[];
  /** Whether at least one cloud function emits this event. */
  hasCoverage: boolean;
  /** Remediation steps. */
  remediation: string[];
}

export interface GapDetectionReport {
  findings: GapFinding[];
  /** Findings with no manifest event. */
  missingFromManifest: GapFinding[];
  /** Findings in manifest but with no cloud function coverage. */
  uncoveredInCode: GapFinding[];
  /** Findings that are fully resolved (in manifest + covered). */
  resolved: GapFinding[];
  /** Overall completion: resolved / total (0–100). */
  completionPct: number;
  /** Whether ALL G1–G5 gaps are resolved (GA readiness gate). */
  gaReady: boolean;
}

// ─── Remediation library ──────────────────────────────────────────────────────

const REMEDIATION: Readonly<Record<string, string[]>> = {
  user_data_export_completed: [
    'Add `user_data_export_completed` to manifest `events[]` with `safetyTier: 1` and `complianceRef: "GDPR Art.20"`.',
    'Emit the event in `offboard.ts` or the DSAR handler after all export files are written to Cloud Storage.',
    'Dual-write to audit log (Firestore `/auditLogs`) and operational event bus.',
  ],
  user_deletion_completed: [
    'Add `user_deletion_completed` to manifest `events[]` with `safetyTier: 1` and `complianceRef: "GDPR Art.17"`.',
    'Emit the event at the end of the `offboardSync` cloud function after all subcollections are deleted.',
    'Include `uid`, `deletedAt`, `subcollectionsDeleted[]` in payload.',
  ],
  consent_version_migrated: [
    'Add `consent_version_migrated` to manifest `events[]` with `safetyTier: 1` and `complianceRef: "GDPR Art.7"`.',
    'Emit in the consent migration flow when a user\'s `consentHistory` doc is updated to a new policy version.',
    'Include `uid`, `fromVersion`, `toVersion`, `migratedAt` in payload.',
  ],
  plaid_token_refreshed: [
    'Add `plaid_token_refreshed` to manifest `events[]` with `safetyTier: 1` and `complianceRef: "CFPB 1033"`.',
    'Emit whenever a Plaid access token is rotated/refreshed in the connections flow.',
    'Include `uid`, `itemId`, `refreshedAt` (no raw token in payload).',
  ],
  minor_age_gate_enforced: [
    'Add `minor_age_gate_enforced` to manifest `events[]` with `safetyTier: 1` and `complianceRef: "KOSA §3"`.',
    'Emit in the age-gate middleware / `kosaAgeGate.ts` when a minor attempts to access a gated feature.',
    'Include `uid`, `featureId`, `minorAgeGroup`, `enforcedAt` in payload.',
  ],
};

function getRemediation(eventType: string): string[] {
  return (
    REMEDIATION[eventType] ?? [
      `Add \`${eventType}\` to the manifest \`events[]\` array.`,
      'Identify the cloud function responsible for this action and emit the event.',
      'Ensure the event has the correct `safetyTier`, `complianceRef`, and `payloadFields`.',
    ]
  );
}

// ─── Derived gap discovery ────────────────────────────────────────────────────

interface DerivedGap {
  id: string;
  requiredEventType: string;
  complianceRef: string;
  description: string;
  severity: GapSeverity;
}

/**
 * Scan manifest entities for legal obligations that imply required events
 * (beyond the predefined G1–G5). Currently discovers:
 *   - Entities with `legalBasis: 'consent'` → need a consent_granted event
 *   - Entities with `legalBasis: 'legal_obligation'` and audit tag → need audit events
 */
function discoverDerivedGaps(manifest: AtlasManifest): DerivedGap[] {
  const gaps: DerivedGap[] = [];
  const seenTypes = new Set<string>();

  for (const entity of manifest.entities) {
    // Entities with financial PII but no plaid/stripe token refresh event covered
    if (
      entity.piiCategory === 'FINANCIAL' &&
      entity.tags.includes('compliance:cfpb1033') &&
      !seenTypes.has('plaid_token_refreshed')
    ) {
      seenTypes.add('plaid_token_refreshed');
      // Already covered in G4 — skip duplicates
    }

    // Entities tagged age-gated but no age gate event
    if (
      (entity.tags.includes('age-gated') || entity.piiCategory === 'MINOR') &&
      !seenTypes.has('minor_age_gate_enforced')
    ) {
      seenTypes.add('minor_age_gate_enforced');
      // Already covered in G5
    }
  }

  return gaps;
}

// ─── Main detector ────────────────────────────────────────────────────────────

function buildFinding(
  manifest: AtlasManifest,
  gap: { id: string; eventType: string; complianceRef: string; description: string; severity: GapSeverity },
): GapFinding {
  const eventTypeMap = new Map(manifest.events.map((e) => [e.type, e]));
  const manifestEvent = eventTypeMap.get(gap.eventType) ?? null;
  const inManifest = manifestEvent !== null;

  const emittedBy: string[] = [];
  for (const fn of manifest.cloudFunctions) {
    if (fn.emitsEvents?.includes(gap.eventType)) {
      emittedBy.push(fn.name);
    }
  }
  const hasCoverage = emittedBy.length > 0;

  return {
    id: gap.id,
    requiredEventType: gap.eventType,
    complianceRef: gap.complianceRef,
    description: gap.description,
    severity: gap.severity,
    inManifest,
    manifestEvent,
    emittedBy,
    hasCoverage,
    remediation: inManifest && hasCoverage ? [] : getRemediation(gap.eventType),
  };
}

export function detectEventGaps(manifest: AtlasManifest): GapDetectionReport {
  const derivedGaps = discoverDerivedGaps(manifest);

  const allGaps = [
    ...GAP_EVENTS,
    ...derivedGaps.map((g) => ({
      id: g.id,
      eventType: g.requiredEventType,
      complianceRef: g.complianceRef,
      description: g.description,
      severity: g.severity,
    })),
  ];

  const findings = allGaps.map((gap) => buildFinding(manifest, gap));

  const missingFromManifest = findings.filter((f) => !f.inManifest);
  const uncoveredInCode = findings.filter((f) => f.inManifest && !f.hasCoverage);
  const resolved = findings.filter((f) => f.inManifest && f.hasCoverage);

  const completionPct =
    findings.length === 0
      ? 100
      : Math.round((resolved.length / findings.length) * 100);

  // GA readiness: all G1–G5 must be resolved
  const g1g5Ids = new Set(GAP_EVENTS.map((g) => g.id));
  const gaReady = findings
    .filter((f) => g1g5Ids.has(f.id))
    .every((f) => f.inManifest && f.hasCoverage);

  return { findings, missingFromManifest, uncoveredInCode, resolved, completionPct, gaReady };
}

/** Check a single gap event by event type. */
export function checkGapEvent(
  manifest: AtlasManifest,
  eventType: string,
): GapFinding | null {
  const gap = GAP_EVENTS.find((g) => g.eventType === eventType);
  if (!gap) return null;
  return buildFinding(manifest, gap);
}
